import { useEffect, useState, type FormEvent } from "react";
import { X, MessageCircle, Mail, ArrowRight } from "lucide-react";

const WHATSAPP_NUMBER = "5511916338874";

export function openDemoModal() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("open-demo-modal"));
}

type Mode = "choice" | "form";

export function DemoModal() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("choice");
  const [form, setForm] = useState({
    nome: "",
    empresa: "",
    email: "",
    telefone: "",
    mensagem: "",
  });

  useEffect(() => {
    const onOpen = () => {
      setMode("choice");
      setOpen(true);
    };
    window.addEventListener("open-demo-modal", onOpen);
    return () => window.removeEventListener("open-demo-modal", onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const openWhatsapp = (message?: string) => {
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
    const text =
      message ??
      `Olá! Vim pela página ${currentPath} da Seven Analytics e gostaria de agendar um diagnóstico de BPO Financeiro e demonstração.`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setOpen(false);
  };

  const submitForm = (e: FormEvent) => {
    e.preventDefault();
    // Enforce basic limits client-side.
    const nome = form.nome.trim().slice(0, 100);
    const empresa = form.empresa.trim().slice(0, 120);
    const email = form.email.trim().slice(0, 200);
    const telefone = form.telefone.trim().slice(0, 30);
    const mensagem = form.mensagem.trim().slice(0, 800);
    if (!nome || !empresa) return;
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";

    if (email && typeof fetch === "function") {
      void fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: nome,
          email,
          phone: telefone || null,
          company_name: empresa,
          source_page: currentPath,
          subject: `Solicitação de Diagnóstico - ${currentPath}`,
          message: mensagem || `Solicitação de diagnóstico iniciada na página ${currentPath} para a empresa ${empresa}.`,
        }),
      }).catch(() => undefined);
    }

    const msg = [
      "Olá! Quero agendar uma demonstração da Seven.",
      "",
      `Nome: ${nome}`,
      `Empresa: ${empresa}`,
      `Página de origem: ${currentPath}`,
      email && `E-mail: ${email}`,
      telefone && `Telefone: ${telefone}`,
      mensagem && `\nContexto: ${mensagem}`,
    ]
      .filter(Boolean)
      .join("\n");
    openWhatsapp(msg);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 text-left"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-modal-title"
    >
      <button
        type="button"
        aria-label="Fechar"
        onClick={() => setOpen(false)}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      />

      <div className="relative w-full max-w-lg bg-background border border-border shadow-[var(--shadow-elevation)] animate-in fade-in zoom-in-95 duration-200">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 text-text-3 hover:text-foreground transition"
          aria-label="Fechar"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-7 md:px-9 pt-9 pb-3">
          <div className="text-[11px] font-display font-semibold tracking-[0.32em] text-bronze">
            AGENDAR DEMONSTRAÇÃO
          </div>
          <h3
            id="demo-modal-title"
            className="mt-3 font-display font-bold text-2xl md:text-3xl leading-tight tracking-[-0.02em]"
          >
            {mode === "choice" ? (
              <>Como prefere <span className="text-bronze">falar</span> com a gente?</>
            ) : (
              <>Conta um <span className="text-bronze">pouco</span> sobre você.</>
            )}
          </h3>
          <p className="mt-3 text-sm text-text-3 leading-relaxed">
            {mode === "choice"
              ? "Escolha o canal mais confortável — respondemos em minutos no horário comercial."
              : "Preencha o formulário e enviamos direto para o WhatsApp do time comercial."}
          </p>
        </div>

        {mode === "choice" && (
          <div className="px-7 md:px-9 pb-8 grid gap-3">
            <button
              type="button"
              onClick={() => openWhatsapp()}
              className="group flex items-center justify-between gap-4 border border-border-strong hover:border-bronze hover:bg-surface-2 transition p-5 text-left"
            >
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 grid place-items-center bg-[#25D366]/10 text-[#128C7E]">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <div>
                  <div className="font-display font-semibold text-base">
                    Falar no WhatsApp
                  </div>
                  <div className="text-xs text-text-3 mt-0.5">
                    Resposta rápida com o comercial
                  </div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-text-3 group-hover:text-bronze group-hover:translate-x-0.5 transition" />
            </button>

            <button
              type="button"
              onClick={() => setMode("form")}
              className="group flex items-center justify-between gap-4 border border-border-strong hover:border-bronze hover:bg-surface-2 transition p-5 text-left"
            >
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 grid place-items-center bg-bronze/10 text-bronze">
                  <Mail className="w-5 h-5" />
                </span>
                <div>
                  <div className="font-display font-semibold text-base">
                    Preencher formulário
                  </div>
                  <div className="text-xs text-text-3 mt-0.5">
                    Envie contexto — retornamos com uma agenda
                  </div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-text-3 group-hover:text-bronze group-hover:translate-x-0.5 transition" />
            </button>
          </div>
        )}

        {mode === "form" && (
          <form onSubmit={submitForm} className="px-7 md:px-9 pb-8 grid gap-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field
                label="Nome"
                value={form.nome}
                onChange={(v) => setForm({ ...form, nome: v })}
                required
                maxLength={100}
              />
              <Field
                label="Empresa"
                value={form.empresa}
                onChange={(v) => setForm({ ...form, empresa: v })}
                required
                maxLength={120}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field
                label="E-mail"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                maxLength={200}
              />
              <Field
                label="Telefone"
                type="tel"
                value={form.telefone}
                onChange={(v) => setForm({ ...form, telefone: v })}
                maxLength={30}
              />
            </div>
            <div>
              <label className="text-[11px] font-display font-semibold tracking-[0.22em] text-text-3 uppercase">
                Contexto
              </label>
              <textarea
                value={form.mensagem}
                onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                maxLength={800}
                rows={3}
                placeholder="Quantas empresas? Já usa alguma ferramenta?"
                className="mt-1.5 w-full bg-surface-2 border border-border focus:border-bronze focus:outline-none px-3 py-2.5 text-sm resize-none"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setMode("choice")}
                className="text-xs font-display font-semibold tracking-widest text-text-3 hover:text-foreground transition"
              >
                ← VOLTAR
              </button>
              <button
                type="submit"
                className="ml-auto group inline-flex items-center gap-2 bg-bronze hover:bg-bronze-hover text-white px-6 py-3 text-[13px] font-semibold tracking-wide transition-all hover:-translate-y-0.5 shadow-[var(--shadow-bronze)]"
              >
                Enviar via WhatsApp
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  maxLength?: number;
}) {
  return (
    <div>
      <label className="text-[11px] font-display font-semibold tracking-[0.22em] text-text-3 uppercase">
        {label}
        {required && <span className="text-bronze">*</span>}
      </label>
      <input
        type={type}
        required={required}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-surface-2 border border-border focus:border-bronze focus:outline-none px-3 py-2.5 text-sm"
      />
    </div>
  );
}
